import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, ArrowRight, ShoppingBag } from "lucide-react";
import userAPI from "../../api/UserApi";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { setAccessToken } from "../../api/axiosInstance";
import { showSuccess } from "../../utils/toastUtils";

export default function RegisterPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";

    if (!formData.email) {
      newErrors.email = "Email address is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { confirmPassword, ...payload } = formData;
      const res = await userAPI.register(payload);
      if (res.code === 200 || res.code === 201) {
        showSuccess(res.message);
        if (res.data?.accessToken) {
          setAccessToken(res.data.accessToken);
          localStorage.setItem("token", JSON.stringify({
            email: res.data.email,
            username: res.data.username,
          }));
          localStorage.setItem("accessToken", res.data.accessToken);
          navigate("/users/dashboard");
        } else {
          navigate("/users/login");
        }
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      {/* ── Left decorative panel ── */}
      <div className="auth-panel auth-panel--left">
        <div className="auth-panel__overlay" />
        <div className="auth-panel__content">
          <div className="auth-brand">
            <ShoppingBag size={32} color="#D4AF37" />
            <span>Premium Store</span>
          </div>
          <h2 className="auth-panel__headline">
            Join a community that values quality.
          </h2>
          <p className="auth-panel__sub">
            Create your account to unlock exclusive deals, track orders, and
            enjoy a personalised shopping experience.
          </p>
          <div className="auth-panel__dots">
            <span className="dot" />
            <span className="dot dot--active" />
            <span className="dot" />
          </div>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="auth-panel auth-panel--right">
        <div className="auth-form-box">
          <p className="auth-form-eyebrow">New Member</p>
          <h1 className="auth-form-title">Create Account</h1>
          <p className="auth-form-subtitle">
            Already have an account?{" "}
            <Link to="/users/login" className="auth-link">
              Sign in
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="auth-form" noValidate>

            {/* First Name + Last Name */}
            <div className="auth-field-row">
              <div className="auth-field">
                <label className="auth-label" htmlFor="firstName">
                  First Name <span className="auth-required">*</span>
                </label>
                <div className={`auth-input-wrap ${errors.firstName ? "has-error" : ""}`}>
                  <User size={17} className="auth-input-icon" />
                  <input
                    id="firstName"
                    type="text"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    autoComplete="given-name"
                  />
                </div>
                {errors.firstName && (
                  <span className="auth-error">{errors.firstName}</span>
                )}
              </div>

              <div className="auth-field">
                <label className="auth-label" htmlFor="lastName">
                  Last Name
                </label>
                <div className="auth-input-wrap">
                  <User size={17} className="auth-input-icon" />
                  <input
                    id="lastName"
                    type="text"
                    name="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    autoComplete="family-name"
                  />
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="auth-field">
              <label className="auth-label" htmlFor="email">
                Email Address <span className="auth-required">*</span>
              </label>
              <div className={`auth-input-wrap ${errors.email ? "has-error" : ""}`}>
                <Mail size={17} className="auth-input-icon" />
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                />
              </div>
              {errors.email && (
                <span className="auth-error">{errors.email}</span>
              )}
            </div>

            {/* Password + Confirm Password */}
            <div className="auth-field-row">
              <div className="auth-field">
                <label className="auth-label" htmlFor="password">
                  Password <span className="auth-required">*</span>
                </label>
                <div className={`auth-input-wrap ${errors.password ? "has-error" : ""}`}>
                  <Lock size={17} className="auth-input-icon" />
                  <input
                    id="password"
                    type="password"
                    name="password"
                    placeholder="Min. 6 characters"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                </div>
                {errors.password && (
                  <span className="auth-error">{errors.password}</span>
                )}
              </div>

              <div className="auth-field">
                <label className="auth-label" htmlFor="confirmPassword">
                  Confirm Password <span className="auth-required">*</span>
                </label>
                <div className={`auth-input-wrap ${errors.confirmPassword ? "has-error" : ""}`}>
                  <Lock size={17} className="auth-input-icon" />
                  <input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    placeholder="Repeat password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                  />
                </div>
                {errors.confirmPassword && (
                  <span className="auth-error">{errors.confirmPassword}</span>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <span className="auth-spinner" />
              ) : (
                <>
                  Create Account <ArrowRight size={17} className="ms-2" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}